import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UniverseLinkInput } from "@/components/UniverseLinkInput";
import { CardImage } from "@/components/CardImage";

export function ContactDialog({ open, onOpenChange, initial, onSaved, dd, isGlobal }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) setF(initial || { type: "Customer" }); }, [open, initial]);
  const save = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    if (isGlobal && !f.division_id && !initial?.id) { toast.error("Select division"); return; }
    setSaving(true);
    try {
      let saved;
      if (initial?.id) saved = (await api.put(`/contacts/${initial.id}`, f)).data;
      else saved = (await api.post("/contacts", f)).data;
      if (f.card_scan_id && !initial?.id) api.post(`/card-scan/${f.card_scan_id}/used`, { entity_type: "contact", entity_id: saved?.id }).catch(() => {});
      toast.success("Saved"); onOpenChange(false); onSaved?.(saved);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-dialog" className="max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit Contact" : f.card_scan_id ? "New Contact from card" : "New Contact"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {isGlobal && !initial?.id && (
            <div className="col-span-2 space-y-1.5"><Label className="text-xs">Division *</Label>
              <Select value={f.division_id || ""} onValueChange={(v) => set("division_id", v)}>
                <SelectTrigger className="h-9" data-testid="contact-division"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="unitile">Unitile</SelectItem><SelectItem value="univicoustic">Univicoustic</SelectItem><SelectItem value="uflex">U-Flex</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Name *</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="contact-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={f.type || "Customer"} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="h-9" data-testid="contact-type"><SelectValue /></SelectTrigger>
              <SelectContent>{dd?.contact_type?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Role Tag</Label>
            <Select value={f.role_tag || ""} onValueChange={(v) => set("role_tag", v === "__none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="contact-roletag"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="__none">— None —</SelectItem>{dd?.role_tag?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Company</Label>
            <UniverseLinkInput value={f.company} universeId={f.universe_id} universeCompany={f.universe_company}
              onChange={(v) => setF((p) => ({ ...p, company: v.value, universe_id: v.universe_id, universe_company: v.universe_company }))}
              placeholder="Company" testid="contact-company" />
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Designation</Label><Input value={f.designation || ""} onChange={(e) => set("designation", e.target.value)} className="h-9" data-testid="contact-designation" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><PhoneInput value={f.phone || ""} onChange={(v) => set("phone", v)} testid="contact-phone" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="h-9" data-testid="contact-email" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Event / Exhibition</Label><Input value={f.event_name || ""} onChange={(e) => set("event_name", e.target.value)} placeholder="e.g. ACETECH Mumbai 2026" className="h-9" data-testid="contact-event" /></div>
          {f.card_scan_id && <div className="col-span-2"><CardImage scanId={f.card_scan_id} /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="contact-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
